import {
  Phone,
  Wifi,
  Building2,
  Home,
  Voicemail,
  ArrowRightLeft,
  PauseCircle,
  Network,
  GitBranch,
  Bot,
  Clock,
  HeadphonesIcon,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ChevronRight,
} from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'
import SEOHead from '@/components/SEOHead'

const FAQS = [
  {
    question: 'Is the UK landline network really being switched off?',
    answer: 'Yes. The traditional PSTN copper landline network is being retired by the end of 2026. All UK landlines must move to digital alternatives such as VoIP. Once the switch-off completes, traditional landlines stop working.',
  },
  {
    question: 'What replaces a traditional landline?',
    answer: 'VoIP (Voice over Internet Protocol) replaces a traditional landline. It gives you a UK landline number that works over your internet connection, with call forwarding, voicemail to email, call recording and business features as standard. Silux Call provides this service at www.siluxcall.co.uk.',
  },
  {
    question: 'Can I keep my existing landline number?',
    answer: 'Yes. Existing UK landline numbers can normally be ported across to a VoIP service, so you keep the number your customers and contacts already know.',
  },
  {
    question: 'Do I need a landline to use VoIP?',
    answer: 'No. VoIP works over any broadband or mobile internet connection, including 4G and 5G mobile broadband. You do not need a copper phone line at all.',
  },
]

export default function VoIP() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'VoIP Landline', url: 'https://www.siluxmobile.co.uk/voip' },
  ]

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 space-y-16">
      {/* This page had NO SEO head at all — no title, description or canonical. A page
          with no meta ranks poorly and passes little value through its outbound links,
          which defeats the point of linking to siluxcall.co.uk from here. */}
      <SEOHead
        title="VoIP Landline UK | PSTN Switch-Off 2026 Replacement | Silux Mobile"
        description="The UK landline network switches off by the end of 2026. Replace your landline with VoIP — keep your number, UK landline numbers, call forwarding, voicemail to email and business features. Full service at www.siluxcall.co.uk."
        keywords={[
          'VoIP UK', 'VoIP landline', 'landline replacement UK', 'PSTN switch off 2026',
          'digital voice UK', 'business VoIP UK', 'home VoIP', 'UK landline numbers',
          'keep my landline number', 'voice over IP provider UK', 'cloud phone system UK',
          'landline alternative UK', 'VoIP phone system', 'siluxcall',
        ]}
        canonical="https://www.siluxmobile.co.uk/voip"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />

      {/* LIVE banner. Was "Coming Soon — register your interest"; the service launched, so
          it now says so and sends people straight to the live site. */}
      <div className="flex flex-wrap items-center justify-center gap-3 bg-emerald-50 border border-emerald-300 rounded-2xl px-6 py-4">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        <p className="text-emerald-900 font-semibold text-sm sm:text-base">
          Now live — the Silux VoIP landline service is available at{' '}
          <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            www.siluxcall.co.uk
          </a>
        </p>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-primary-900 to-gray-900 text-white px-4 sm:px-8 py-10 sm:py-16 md:px-16 text-center">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,#38bdf8,transparent_60%),radial-gradient(circle_at_70%_50%,#818cf8,transparent_60%)]" />
        <div className="relative space-y-4 sm:space-y-5">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium text-white/90">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            UK PSTN Switch-Off by End of 2026
          </div>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight">
            The Future of Landlines<br className="hidden md:block" /> Is Here
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            The UK's traditional landline network is being switched off by the end of 2026.
            Don't get left behind — upgrade to a smarter, future-proof VoIP system today.
          </p>
          {/* Backlink to the sister VoIP brand. A real in-body anchor with descriptive
              text, opened in a new tab. Deliberately NOT rel="nofollow" — the whole point
              is to pass link equity to siluxcall.co.uk. */}
          <p className="text-base text-white/75 max-w-2xl mx-auto leading-relaxed">
            Our full VoIP landline service runs at{' '}
            <a
              href="https://www.siluxcall.co.uk"
              target="_blank"
              rel="noopener"
              className="font-semibold text-white underline underline-offset-4 hover:text-cyan-200 transition-colors"
            >
              www.siluxcall.co.uk
            </a>
            {' '}— UK landline numbers, call packages and business features.
          </p>
          <div className="flex justify-center gap-4 flex-wrap pt-2">
            <a
              href="https://www.siluxcall.co.uk"
              target="_blank"
              rel="noopener"
              className="px-7 py-3 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Visit Silux Call
            </a>
            <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="px-7 py-3 bg-white/10 border border-white/30 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors"
          >
            Get Started Today
          </a>
            <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="px-7 py-3 bg-white/10 border border-white/30 text-white rounded-xl font-semibold hover:bg-white/20 transition-colors"
          >
            Talk to an Expert
          </a>
          </div>
        </div>
      </div>

      <SiluxPromise variant="all" />

      {/* What's Happening */}
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div className="space-y-4">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">The Switch-Off Explained</span>
          <h2 className="text-3xl font-bold text-gray-900">Why Your Landline Is Being Retired</h2>
          <p className="text-gray-600 leading-relaxed">
            The <strong>Public Switched Telephone Network (PSTN)</strong> has powered homes and businesses
            for decades using analogue lines. Now, it's being permanently retired and replaced with faster,
            more flexible internet-based calling.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Once the switch-off completes, traditional landlines will simply stop working. The time to
            act is now — before the deadline forces the issue.
          </p>
          <div className="flex items-center gap-3 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-sm text-yellow-800 font-medium">
              All UK landlines must be migrated to digital alternatives by the end of 2026.
            </p>
          </div>
        </div>
        <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-2xl p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
              <Wifi className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">That's where we come in.</p>
              <p className="text-sm text-gray-500">Upgrade to Smart VoIP Calling</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Our VoIP (Voice over Internet Protocol) service gives you everything a traditional landline
            offered — and far more. Whether you're running a business or simply replacing your home phone,
            you'll benefit from a modern system designed around flexibility, control, and cost-efficiency.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-1">
            {[
              'Crystal-clear HD voice',
              'Works over broadband',
              'Keep your number',
              'No physical lines needed',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Choose Your Number */}
      <div className="bg-gray-900 text-white rounded-3xl px-8 py-12 md:px-14">
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-10">
          <Phone className="w-10 h-10 text-cyan-400 mx-auto" />
          <h2 className="text-3xl font-bold">Choose Your Perfect Number</h2>
          <p className="text-gray-400">Select the right number to match your identity — personal or professional.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Network className="w-5 h-5" />, text: 'Select UK landline numbers from anywhere in the country' },
            { icon: <Building2 className="w-5 h-5" />, text: 'Perfect for businesses wanting a local presence' },
            { icon: <Home className="w-5 h-5" />, text: 'Ideal for home users replacing old landlines' },
            { icon: <Sparkles className="w-5 h-5" />, text: 'Option to secure memorable or personalised numbers' },
          ].map(({ icon, text }) => (
            <div key={text} className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3">
              <div className="w-9 h-9 bg-cyan-500/20 rounded-xl flex items-center justify-center text-cyan-400">
                {icon}
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Business vs Home */}
      <div>
        <div className="text-center space-y-2 mb-10">
          <h2 className="text-3xl font-bold text-gray-900">Use It Your Way</h2>
          <p className="text-gray-500">Designed for both professional environments and everyday home use.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Business */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-primary-600 to-cyan-600 px-6 py-5 flex items-center gap-3">
              <Building2 className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">For Business</h3>
            </div>
            <div className="p-6 space-y-4">
              {[
                { icon: <ArrowRightLeft className="w-5 h-5 text-primary-500" />, title: 'Smart Call Routing', desc: 'Route calls to your mobile or team instantly' },
                { icon: <GitBranch className="w-5 h-5 text-primary-500" />, title: 'Professional Call Flows', desc: 'Hunting groups, forwarding, and advanced routing' },
                { icon: <TrendingUp className="w-5 h-5 text-primary-500" />, title: 'Polished Image', desc: 'Present an established, professional presence to customers' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-9 h-9 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{title}</p>
                    <p className="text-sm text-gray-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Home */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 flex items-center gap-3">
              <Home className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">For Home</h3>
            </div>
            <div className="p-6 space-y-4">
              {[
                { icon: <Phone className="w-5 h-5 text-emerald-500" />, title: 'Landline Replacement', desc: 'Replace your traditional landline with a smarter alternative' },
                { icon: <ArrowRightLeft className="w-5 h-5 text-emerald-500" />, title: 'Mobile Forwarding', desc: 'Forward calls directly to your mobile — stay reachable anywhere' },
                { icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />, title: 'Keep Your Number', desc: 'Retain a fixed home number without physical phone lines' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{title}</p>
                    <p className="text-sm text-gray-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-gradient-to-br from-primary-50 to-cyan-50 rounded-3xl px-8 py-12 md:px-14">
        <div className="text-center space-y-2 mb-10">
          <h2 className="text-3xl font-bold text-gray-900">Powerful Features as Standard</h2>
          <p className="text-gray-500">Everything you need included — no hidden extras.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: <Voicemail className="w-5 h-5" />, label: 'Voicemail', desc: 'Never miss a message' },
            { icon: <ArrowRightLeft className="w-5 h-5" />, label: 'Call Forwarding', desc: 'Redirect calls anywhere' },
            { icon: <PauseCircle className="w-5 h-5" />, label: 'Call Holding', desc: 'Professional call management' },
            { icon: <Network className="w-5 h-5" />, label: 'Hunting Groups', desc: 'Ring multiple numbers at once' },
            { icon: <GitBranch className="w-5 h-5" />, label: 'Advanced Call Routing', desc: 'Full control over call flows' },
            { icon: <HeadphonesIcon className="w-5 h-5" />, label: 'HD Voice Quality', desc: 'Crystal-clear conversations' },
          ].map(({ icon, label, desc }) => (
            <div key={label} className="bg-white rounded-2xl p-5 flex gap-4 shadow-sm border border-white">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 flex-shrink-0">
                {icon}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{label}</p>
                <p className="text-sm text-gray-500">{desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-6">
          All features designed to give you complete control over how calls are handled.
        </p>
      </div>

      {/* AI Options */}
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div className="bg-gradient-to-br from-violet-600 to-indigo-700 rounded-2xl p-8 text-white space-y-6">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-2xl font-bold">Next-Generation AI Options</h3>
          <p className="text-violet-100 text-sm leading-relaxed">
            Take your setup even further with optional AI-powered features — from simple call answering to
            fully automated conversations. You decide how far it goes.
          </p>
          <div className="space-y-3">
            {[
              { icon: <Clock className="w-4 h-4" />, text: '24/7 AI receptionists' },
              { icon: <HeadphonesIcon className="w-4 h-4" />, text: 'Automated customer support' },
              { icon: <TrendingUp className="w-4 h-4" />, text: 'AI sales assistance & enquiry handling' },
              { icon: <Sparkles className="w-4 h-4" />, text: 'Fully customisable to your needs' },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-3 text-sm text-violet-100">
                <div className="text-violet-300">{icon}</div>
                {text}
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="space-y-6">
          <div className="space-y-2">
            <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">Pricing</span>
            <h2 className="text-3xl font-bold text-gray-900">Simple, Flexible Pricing</h2>
            <p className="text-gray-500">No complicated pricing. Just clear, affordable plans.</p>
          </div>
          <div className="border-2 border-primary-500 rounded-2xl p-6 space-y-4 bg-primary-50">
            <div>
              <span className="text-4xl font-bold text-gray-900">£5.99</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-sm text-gray-600">Starting price — includes core VoIP features.</p>
            <div className="space-y-2">
              {[
                'Pay-as-you-go options with no long-term contracts',
                'Bespoke packages available for advanced requirements',
                'AI features available as optional add-ons',
              ].map((item) => (
                <div key={item} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
          >
            Get a Quote <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-primary-600 via-cyan-600 to-primary-700 rounded-3xl px-8 py-12 text-center text-white space-y-5">
        <h2 className="text-3xl font-bold">Ready for the Switch-Off?</h2>
        <p className="text-white/85 max-w-xl mx-auto leading-relaxed">
          Don't wait until your old landline stops working. Move to a smarter, future-proof system today —
          whether for your business or your home — and take full control of your communications.
          See plans and numbers at{' '}
          <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="font-semibold text-white underline underline-offset-4 hover:text-cyan-100 transition-colors"
          >
            www.siluxcall.co.uk
          </a>.
        </p>
        <div className="flex justify-center gap-4 flex-wrap">
          <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="px-7 py-3 bg-white text-primary-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
          >
            Switch Today
          </a>
          <a
            href="https://www.siluxcall.co.uk"
            target="_blank"
            rel="noopener"
            className="px-7 py-3 bg-white/15 border border-white/30 text-white rounded-xl font-semibold hover:bg-white/25 transition-colors"
          >
            Speak to Our Team
          </a>
        </div>
      </div>

    </div>
  )
}
